import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  NotificationListItem,
  NotificationStatus,
  NotificationType,
  ListNotificationsParams,
} from '../../api/notifications';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Loader2, Search, ChevronLeft, ChevronRight, Eye, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface NotificationsPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 20;

export default function NotificationsPage({ isDark }: NotificationsPageProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<ListNotificationsParams>({ page: 1, limit: ITEMS_PER_PAGE });
  const [notificationToDelete, setNotificationToDelete] = useState<NotificationListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await notificationsApi.list({ ...filters, page: currentPage });
      setNotifications(data.notifications);
      setTotalPages(data.pagination.total_pages);
      setTotal(data.pagination.total);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const getStatusBadge = (status: NotificationStatus) => {
    const variants: Record<NotificationStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      [NotificationStatus.PENDING]: { variant: 'secondary', label: 'Pending' },
      [NotificationStatus.SCHEDULED]: { variant: 'default', label: 'Scheduled' },
      [NotificationStatus.SENT]: { variant: 'default', label: 'Sent' },
      [NotificationStatus.FAILED]: { variant: 'destructive', label: 'Failed' },
      [NotificationStatus.CANCELLED]: { variant: 'outline', label: 'Cancelled' },
    };
    const config = variants[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeBadge = (type: NotificationType) => {
    const colors: Record<NotificationType, string> = {
      [NotificationType.SYSTEM]: 'bg-blue-500',
      [NotificationType.PAYMENT]: 'bg-green-500',
      [NotificationType.SUBSCRIPTION]: 'bg-purple-500',
      [NotificationType.PROMO]: 'bg-orange-500',
      [NotificationType.REMINDER]: 'bg-yellow-500',
      [NotificationType.ACHIEVEMENT]: 'bg-pink-500',
    };
    return <Badge className={cn('text-white', colors[type])}>{type.charAt(0).toUpperCase() + type.slice(1)}</Badge>;
  };

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this scheduled notification?')) return;
    try {
      await notificationsApi.cancel(id);
      toast.success('Notification cancelled');
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel notification');
    }
  };

  const handleConfirmDelete = async () => {
    if (!notificationToDelete) return;
    setIsDeleting(true);
    try {
      await notificationsApi.delete(notificationToDelete.id);
      toast.success('Notification deleted for all users');
      setNotificationToDelete(null);
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>Notifications</h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Manage push notifications for your users</p>
        </div>
        <Button onClick={() => navigate('/notifications/create')} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
          <Plus className="w-4 h-4 mr-2" />
          Create Notification
        </Button>
      </div>

      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className={cn('absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
              <Input placeholder="Search notifications..." value={filters.search || ''} onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className={cn('pl-10', isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
            </div>
            <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? undefined : (value as NotificationStatus), page: 1 })}>
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value={NotificationStatus.PENDING}>Pending</SelectItem>
                <SelectItem value={NotificationStatus.SCHEDULED}>Scheduled</SelectItem>
                <SelectItem value={NotificationStatus.SENT}>Sent</SelectItem>
                <SelectItem value={NotificationStatus.FAILED}>Failed</SelectItem>
                <SelectItem value={NotificationStatus.CANCELLED}>Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.type || 'all'} onValueChange={(value) => setFilters({ ...filters, type: value === 'all' ? undefined : (value as NotificationType), page: 1 })}>
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value={NotificationType.SYSTEM}>System</SelectItem>
                <SelectItem value={NotificationType.PAYMENT}>Payment</SelectItem>
                <SelectItem value={NotificationType.SUBSCRIPTION}>Subscription</SelectItem>
                <SelectItem value={NotificationType.PROMO}>Promo</SelectItem>
                <SelectItem value={NotificationType.REMINDER}>Reminder</SelectItem>
                <SelectItem value={NotificationType.ACHIEVEMENT}>Achievement</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setFilters({ page: 1, limit: ITEMS_PER_PAGE }); setCurrentPage(1); }}
              className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        {isLoading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-[#0EA5A4]" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center p-12"><p className={cn('text-lg', isDark ? 'text-slate-400' : 'text-slate-500')}>No notifications found</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={cn(isDark ? 'border-slate-700 hover:bg-slate-800' : 'hover:bg-slate-50')}>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Title</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Type</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Status</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Recipients</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Sent / Opened</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Open Rate</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Created</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notification) => (
                    <TableRow key={notification.id} className={cn(isDark ? 'border-slate-700 hover:bg-slate-800' : 'hover:bg-slate-50')}>
                      <TableCell className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
                        <div>
                          <div className="font-medium">{notification.title}</div>
                          <div className={cn('text-xs mt-1 truncate max-w-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{notification.body}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getTypeBadge(notification.type)}</TableCell>
                      <TableCell>{getStatusBadge(notification.status)}</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{notification.estimated_recipients?.toLocaleString() || '-'}</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{notification.sent_count.toLocaleString()} / {notification.opened_count.toLocaleString()}</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{notification.open_rate.toFixed(1)}%</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{format(new Date(notification.created_at), 'MMM d, yyyy HH:mm')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/notifications/${notification.id}/analytics`)}
                            className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : '')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {notification.status === NotificationStatus.SCHEDULED && (
                            <Button variant="ghost" size="sm" onClick={() => handleCancel(notification.id)}
                              className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : '')}>
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setNotificationToDelete(notification)}
                            className={cn(
                              'text-red-500 hover:text-red-600',
                              isDark ? 'hover:bg-slate-700' : 'hover:bg-red-50',
                            )}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className={cn('flex items-center justify-between p-4 border-t', isDark ? 'border-slate-700' : 'border-slate-200')}>
              <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total} notifications
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className={cn('text-sm px-3', isDark ? 'text-slate-300' : 'text-slate-600')}>Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <AlertDialog
        open={!!notificationToDelete}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setNotificationToDelete(null);
        }}
      >
        <AlertDialogContent className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}>
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(isDark ? 'text-white' : '')}>
              Ты уверен удалить это уведомление?
            </AlertDialogTitle>
            <AlertDialogDescription className={cn(isDark ? 'text-slate-400' : '')}>
              «{notificationToDelete?.title}» будет удалено у всех пользователей вместе со
              статистикой отправки и отметками о прочтении. Действие необратимо.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeleting}
              className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : '')}
            >
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Удаление…
                </>
              ) : (
                'Удалить'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
